/**
 * Shared wording for the organization-workflow consequence surfaces
 * (DC-1802).
 *
 * Two surfaces state the same facts to different people — the administrator
 * in the Settings confirmation, and the non-admin member in the shell notice
 * after a flip. They must not drift into two vocabularies for one event, so
 * the phrasing lives here once.
 *
 * Deliberate wording choice: switching a workflow REMOVES NAVIGATION, it does
 * not delete work. Saying "you will lose 62 analyses" would be false and
 * would make an administrator distrust every other warning the product gives
 * them. Saying nothing at all is what caused the 2026-08-17 incident.
 */

/** How long after a change the shell keeps explaining it to non-admins. */
export const WORKFLOW_CHANGE_NOTICE_WINDOW_DAYS = 14;

/** "8 datasets" / "1 dataset" / "no datasets" */
function countPhrase(count: number, singular: string, plural: string): string {
  if (count === 0) return `no ${plural}`;
  if (count === 1) return `1 ${singular}`;
  return `${count} ${plural}`;
}

/** "8 datasets and 62 analyses" — the holdings clause, without a subject. */
export function formatHoldings(holdings: {
  datasets: number;
  analyses: number;
}): string {
  return `${countPhrase(holdings.datasets, 'dataset', 'datasets')} and ${countPhrase(
    holdings.analyses,
    'analysis',
    'analyses'
  )}`;
}

/** The date a change happened, in the reader's own timezone. */
export function formatChangedAt(isoInstant: string): string {
  const at = new Date(isoInstant);
  if (Number.isNaN(at.getTime())) return 'an unrecorded date';
  return at.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Who performed a change, for prose. Falls back through name → email →
 * an explicit anonymous phrase; never renders "null" or an empty subject.
 */
export function formatChangedBy(provenance: {
  changedByName: string | null;
  changedByEmail: string | null;
}): string {
  return (
    provenance.changedByName?.trim() ||
    provenance.changedByEmail?.trim() ||
    'an administrator whose account is no longer listed'
  );
}

/** True while a change is still recent enough for the shell to explain it. */
export function isWithinNoticeWindow(isoInstant: string, now: Date = new Date()): boolean {
  const at = new Date(isoInstant);
  if (Number.isNaN(at.getTime())) return false;
  const elapsedDays = (now.getTime() - at.getTime()) / 86_400_000;
  return elapsedDays >= 0 && elapsedDays <= WORKFLOW_CHANGE_NOTICE_WINDOW_DAYS;
}
